import React, { useCallback, useEffect, useState } from "react";

import { Col, Row } from "react-bootstrap";

import { isArray, isEmpty, isUndefined } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useTypedSearchParams } from "react-router-typesafe-routes/dom";
import { useRecoilState } from "recoil";

import { ArtistResaleInput, ArtworkInput, Participant, ParticipantTypes, ResaleDataInput } from "@api/graphql";
import { validateResaleStatus } from "@common/operations/validateResale";
import { ResaleModal } from "@components/ResaleModal";
import { FormStep, MultiStepForm } from "@components/form/MultiStepForm";
import {
  isAuthUserAllowed,
  useIsAuthUserAllowed,
  useIsAuthorisedUser,
} from "@components/permission_control/ProtectedComponent";
import { ArtistDetails } from "@components/resale_submission/ArtistDetails";
import { ArtworkDetails } from "@components/resale_submission/ArtworkDetails";
import { ResaleDetails } from "@components/resale_submission/ResaleDetails";
import { ReviewResale } from "@components/resale_submission/ReviewResale";
import { SelectParticipant } from "@components/resale_submission/SelectParticipant";
import { ValidatedResaleInput } from "@components/resale_submission/csv/ParseFiles";
import { Role } from "@roles";
import { resaleFormData, selectedParticipant } from "@states/resale_submission";

import { ROUTES } from "../../routes";

import { DashboardHeader } from "./DashboardHeader";

import styles from "./ResaleSubmission.module.scss";

type ResaleSubmissionDataIntersection = ArtistResaleInput & ArtworkInput & ResaleDataInput;

const EMPTY_RESALE_INPUT: ValidatedResaleInput = {
  batch_discriminator: undefined,
  resale_id: undefined,
  artist: {} as ArtistResaleInput,
  artwork: {} as ArtworkInput,
  resale: {} as ResaleDataInput,
  resale_status: undefined,
  opt_out_expiry: undefined,
};

export const ResaleSubmission = () => {
  const isCMO = useIsAuthorisedUser({ requiredParticipantTypes: [ParticipantTypes.CMO] });
  const navigate = useNavigate();
  if (isCMO) {
    navigate(ROUTES.Dashboard.path);
  }
  const [showModal, setShowModal] = useState<boolean>(false);
  const [resaleFormState, setResaleFormState] = useRecoilState(resaleFormData);
  const [{ artistBatchDiscriminator }] = useTypedSearchParams(ROUTES.ResaleSubmission.ArtworkDetails);
  const [{ index_id }] = useTypedSearchParams(ROUTES.ResaleSubmission);
  const [currentFormData, setCurrentFormData] = useState({} as ValidatedResaleInput);
  const internal = useIsAuthUserAllowed([Role.Data, Role.VisualArtistAdmin]);
  const [newParticipant, setNewParticipant] = useState<Participant | undefined>(useLocation().state?.participant);
  const [chosenParticipant, setChosenParticipant] = useRecoilState(selectedParticipant);
  const recievedParticipant = useLocation().state?.participant;
  const [initialMultiFormData, setInitialMultiFormData] = useState(
    !isUndefined(index_id) ? resaleFormState[index_id] : EMPTY_RESALE_INPUT,
  );

  if (recievedParticipant !== undefined && newParticipant === undefined) {
    setNewParticipant(recievedParticipant);
  }

  const participant_step = {
    route: ROUTES.ResaleSubmission.$.SelectParticipant.relativePath,
    displayName: "Select Participant",
    component: SelectParticipant,
    transitionSubmit: () => ROUTES.ResaleSubmission.$.ArtistDetails.relativePath,
  } as FormStep<ValidatedResaleInput, ArtistResaleInput>;

  const artist_step = {
    route: ROUTES.ResaleSubmission.$.ArtistDetails.relativePath,
    displayName: "Artist details",
    component: ArtistDetails,
    transitionSubmit: () => ROUTES.ResaleSubmission.$.ArtworkDetails.relativePath,
    mergeKey: "artist",
  } as FormStep<ValidatedResaleInput, ArtistResaleInput>;

  const artwork_step = {
    route: ROUTES.ResaleSubmission.$.ArtworkDetails.relativePath,
    displayName: "Artwork details",
    component: ArtworkDetails,
    transitionCancel: () => ROUTES.ResaleSubmission.$.ArtistDetails.relativePath,
    transitionSubmit: () => ROUTES.ResaleSubmission.$.ResaleDetails.relativePath,
    mergeKey: "artwork",
  } as FormStep<ValidatedResaleInput, ArtworkInput>;

  const resale_step = {
    route: ROUTES.ResaleSubmission.$.ResaleDetails.relativePath,
    displayName: "Resale details",
    component: ResaleDetails,
    transitionCancel: () => ROUTES.ResaleSubmission.$.ArtworkDetails.relativePath,
    transitionSubmit: () => ROUTES.ResaleSubmission.$.ReviewResale.relativePath,
    mergeKey: "resale",
  } as FormStep<ValidatedResaleInput, ResaleDataInput>;

  const review_step = {
    route: ROUTES.ResaleSubmission.$.ReviewResale.relativePath,
    displayName: "Review",
    component: ReviewResale,
    transitionCancel: () => ROUTES.ResaleSubmission.$.ResaleDetails.relativePath,
  } as FormStep<ValidatedResaleInput, ResaleSubmissionDataIntersection>;

  const steps = [
    ...(internal && !chosenParticipant ? [participant_step, artist_step] : [artist_step]),
    artwork_step,
    resale_step,
    review_step,
  ];

  useEffect(() => {
    if (isArray(resaleFormState) && !isEmpty(resaleFormState)) {
      if (artistBatchDiscriminator) {
        const artist = resaleFormState.find((resale) => resale.batch_discriminator == artistBatchDiscriminator)?.artist;

        if (artist) {
          setInitialMultiFormData({ ...EMPTY_RESALE_INPUT, artist });
        } else if (index_id !== undefined) {
          setInitialMultiFormData(resaleFormState[index_id]);
        } else {
          setInitialMultiFormData(EMPTY_RESALE_INPUT);
        }
      }
      !isUndefined(index_id) && setInitialMultiFormData(resaleFormState[index_id]);
    }
  }, [artistBatchDiscriminator, index_id, resaleFormState]);

  const handleSubmit = useCallback(
    (multiFormData: ValidatedResaleInput) => {
      const batch_discriminator = multiFormData.batch_discriminator;
      multiFormData.validation = {
        success: true,
        issues: [],
      };

      multiFormData.resaleStatus = isAuthUserAllowed([Role.Data])
        ? validateResaleStatus(
            new Date(multiFormData.resale.artwork_sale_date),
            multiFormData.resale.artwork_sale_price,
            multiFormData.resale.acquired_date_choice,
            multiFormData.artist,
          )
        : undefined;

      if (newParticipant !== undefined) {
        setChosenParticipant(newParticipant);
      }
      if (!batch_discriminator) {
        multiFormData.batch_discriminator = crypto.randomUUID();
        setResaleFormState([...resaleFormState, multiFormData]);
      } else {
        const updatedState = resaleFormState.map((resale) =>
          resale.batch_discriminator === batch_discriminator ? multiFormData : resale,
        );
        setResaleFormState(updatedState);
      }
      setCurrentFormData(multiFormData);
      setShowModal(true);
    },
    [resaleFormState, setResaleFormState, newParticipant, setChosenParticipant],
  );

  const handleCloseModal = () => setShowModal(false);

  return (
    <>
      <ResaleModal isOpen={showModal} closeModal={handleCloseModal} formData={currentFormData} />
      <DashboardHeader></DashboardHeader>
      <div className={styles["content"]}>
        <Row className="justify-content-center">
          <Col xs={12} sm={12} md={10} lg={8} className="pt-5">
            {MultiStepForm<ValidatedResaleInput, ResaleSubmissionDataIntersection>({
              baseRoute: ROUTES.ResaleSubmission.path,
              steps,
              initialMultiFormData,
              onSubmit: handleSubmit,
            })}
          </Col>
        </Row>
      </div>
    </>
  );
};
